import React, { useRef, useState } from "react";
import { SITE_CONTENT } from "../../constants/siteContent";
import { submitContactInquiry } from "../../services/contactService";
import { trackFormEvent } from "../../utils/analytics";
import {
  whatsappUrl,
  mailUrl,
  telUrl,
  phoneDisplay,
  responseNote,
  EXTERNAL_LINK_PROPS
} from "../../utils/contact";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import Section, { SectionHead } from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import styles from "./ContactPage.module.css";

/* ContactPage — /contact
 *
 * Surface, vertical rhythm and container are owned by <Section>; this module
 * styles only what lives inside. Two surfaces: a PAPER hero (title + the real,
 * direct channels) and an inset OLIVE PANEL that carries the form on a paper
 * <TicketCard>. The ticket re-declares the --section-* context for its subtree,
 * so every field, hint and error inside it is ink-on-paper by construction even
 * though the Section around it is olive.
 *
 * The site header is fixed and transparent with no bar of its own, so the hero
 * reserves var(--header-h) above its copy (.hero in the module).
 *
 * Anti-spam matches the homepage lead form
 * exactly: an invisible honeypot plus a minimum time-on-form, so a real person
 * never has to solve a puzzle. The localStorage cooldown, the duplicate guard
 * and every field rule from the previous build are preserved.
 *
 * The form is one route, not the only one. WhatsApp leads the channel row in
 * the hero and sits beside the submit button as a stated alternative, because
 * plenty of buyers will send a message but never fill in a form. It is always a
 * text link, never a second primary button.
 */

/* Real contact details, read from SITE_CONTENT -- never invented here. */
const CONTACT = SITE_CONTENT.contact || {};
const CONTACT_EMAIL = CONTACT.email || "";
const CONTACT_HOURS = CONTACT.hoursNote || "";
const CONTACT_LOCATIONS = ((SITE_CONTENT.footer || {}).locations || []).filter(Boolean);

/* src/utils/contact.js is the one place that builds a wa.me / tel: / mailto:
   link, so this page can never drift from the header, the footer or the lead
   form on the number or on the pre-filled opener. Every builder returns "" when
   that channel is not configured, and "" means render nothing at all -- a dead
   link is worse than no link. */
const WA_TALK_URL = whatsappUrl("call"); /* opener: book a 30-minute call */
const WA_BRIEF_URL = whatsappUrl("quote"); /* opener: fixed-price quote */
const MAIL_URL = mailUrl("Project inquiry");
const TEL_URL = telUrl();
const PHONE_DISPLAY = phoneDisplay();
const RESPONSE_NOTE = responseNote();

/* 22 characters, so the label can never wrap onto a second line. */
const WHATSAPP_LABEL = "Message us on WhatsApp";

const CHANNELS = [
  WA_TALK_URL
    ? {
        /* "support" is the icon set's reach-a-human glyph. The set ships no
           messaging icon and inlining a brand SVG is not allowed. */
        icon: "support",
        label: "WhatsApp",
        value: "Message us",
        href: WA_TALK_URL,
        external: true,
        /* WCAG 2.5.3: the accessible name opens with the visible link text
           ("Message us"), then names the channel and flags the new tab. */
        ariaLabel: `${WHATSAPP_LABEL}, opens in a new tab`,
        note: RESPONSE_NOTE
      }
    : null,
  CONTACT_EMAIL && MAIL_URL
    ? {
        icon: "mail",
        label: "Email us",
        value: CONTACT_EMAIL,
        href: MAIL_URL
      }
    : null,
  PHONE_DISPLAY && TEL_URL
    ? {
        icon: "phone",
        label: "Call us",
        value: PHONE_DISPLAY,
        href: TEL_URL,
        note: CONTACT_HOURS
      }
    : null,
  CONTACT_LOCATIONS.length
    ? {
        icon: "pin",
        label: "Where we are",
        value: CONTACT_LOCATIONS.join(", "),
        href: ""
      }
    : null
].filter(Boolean);

// TODO(content): contact page copy
const COPY = {
  eyebrow: "Contact",
  /* Headlines are { before, accent, after }: the same sentence as ever, with
     the ONE word in `accent` set in the serif. Leave `after` empty when the
     accent closes the line. */
  title: { before: "Let’s", accent: "talk", after: "about what you want to build" },
  lead:
    "Send us a short message. We reply within one business day, set up a 30-minute call, and tell you honestly whether we are the right team — and what it will cost if we are.",
  formEyebrow: "Project brief",
  formTitle: { before: "Tell us what you want to", accent: "build", after: "" },
  formLead: "Three quick fields. We will reply within one business day with a time to talk.",
  submit: "Send project brief",
  sending: "Sending…",
  busy: "Sending your project brief…",
  success: "Inquiry submitted successfully. Our team will contact you within 1 business day.",
  privacy: "Your details stay private and are only used to reply to you."
};

/* { before, accent, after } -> "BEFORE <em>ACCENT</em> AFTER". Statement and
   SectionHead both turn the <em> into the serif accent word, coloured by the
   surface (--ink on paper, --lime-ink on olive — never lime on paper). */
function accented(parts) {
  return (
    <>
      {parts.before ? `${parts.before} ` : ""}
      <em>{parts.accent}</em>
      {parts.after ? ` ${parts.after}` : ""}
    </>
  );
}

/* Invisible anti-spam — identical to the homepage lead form. A bot fills every
   field it can see (including the honeypot) and posts within milliseconds; a
   person does neither. */
const MIN_FILL_MS = 3000;

/* submitContactInquiry(formData, captchaToken) keeps its signature. This form
   no longer ships a reCAPTCHA widget, so it sends the same benign, non-empty
   sentinel the homepage form sends. The Cloud Function only reads that value
   when ENFORCE_RECAPTCHA is switched on server-side; the real spam gate is the
   honeypot (enforced server-side too, via the `website` field) plus the timing
   check below. */
const ANTISPAM_TOKEN = "honeypot-timing";

const INITIAL_FORM = {
  fullName: "",
  workEmail: "",
  company: "",
  service: "",
  budget: "",
  timeline: "",
  details: "",
  website: ""
};

const FIELD_IDS = {
  fullName: "contact-full-name",
  workEmail: "contact-work-email",
  details: "contact-details"
};

/* The order the first invalid field is focused in. */
const FIELD_ORDER = ["fullName", "workEmail", "details"];

const LAST_SUBMIT_KEY = "appinfigo_contact_last_submit";
const LAST_PAYLOAD_KEY = "appinfigo_contact_last_payload";

/* localStorage throws in some privacy modes — a blocked store must never take
   the form down; it only means no client-side cooldown for that visitor. */
function readStore(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStore(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    /* no-op */
  }
}

function ContactPage() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const hasTrackedFormStart = useRef(false);
  const startedAt = useRef(Date.now());

  const isSubmitting = status.state === "submitting";

  const validate = () => {
    const nextErrors = {};

    if (!formData.fullName.trim()) {
      nextErrors.fullName = "Please enter your name.";
    }

    if (!formData.workEmail.trim()) {
      nextErrors.workEmail = "Please enter your email.";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.workEmail)) {
      nextErrors.workEmail = "Please enter a valid email address.";
    }

    if (!formData.details.trim()) {
      nextErrors.details = "Tell us briefly what you want to build.";
    }

    return nextErrors;
  };

  const focusFirstError = (validationErrors) => {
    const firstKey = FIELD_ORDER.find((key) => validationErrors[key]);
    if (!firstKey) return;

    const field = document.getElementById(FIELD_IDS[firstKey]);
    if (field) field.focus();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (!hasTrackedFormStart.current) {
      hasTrackedFormStart.current = true;
      trackFormEvent("contact_form_started", { page: "contact" });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    // Honeypot: only a bot can reach this field.
    if (formData.website.trim()) {
      setStatus({
        state: "error",
        message: CONTACT_EMAIL
          ? `We couldn’t verify this submission. Please email ${CONTACT_EMAIL} instead.`
          : "We couldn’t verify this submission. Please try again."
      });
      trackFormEvent("contact_submit_blocked", { reason: "honeypot" });
      return;
    }

    const now = Date.now();
    const previousSubmitAt = Number(readStore(LAST_SUBMIT_KEY) || 0);

    if (previousSubmitAt && now - previousSubmitAt < 60 * 1000) {
      setStatus({
        state: "error",
        message: "Please wait a minute before submitting another inquiry."
      });
      trackFormEvent("contact_submit_blocked", { reason: "cooldown" });
      return;
    }

    const dedupeFingerprint = JSON.stringify({
      fullName: formData.fullName.trim().toLowerCase(),
      workEmail: formData.workEmail.trim().toLowerCase(),
      details: formData.details.trim().toLowerCase()
    });
    const previousFingerprint = readStore(LAST_PAYLOAD_KEY);

    if (previousFingerprint === dedupeFingerprint) {
      setStatus({
        state: "error",
        message: "We already received this inquiry. Update details or try again later."
      });
      trackFormEvent("contact_submit_blocked", { reason: "duplicate_local" });
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatus({
        state: "error",
        message: "Please fix the highlighted fields and submit again."
      });
      trackFormEvent("contact_validation_error", {
        error_count: Object.keys(validationErrors).length,
        has_email_error: Boolean(validationErrors.workEmail),
        has_details_error: Boolean(validationErrors.details)
      });
      focusFirstError(validationErrors);
      return;
    }

    // Time-on-form: an instant post is automated, not human.
    if (now - startedAt.current < MIN_FILL_MS) {
      setStatus({
        state: "error",
        message: "That came through a little too fast — please submit once more."
      });
      trackFormEvent("contact_submit_blocked", { reason: "too_fast" });
      return;
    }

    setErrors({});
    setStatus({ state: "submitting", message: COPY.busy });
    trackFormEvent("contact_submit_attempt", {
      service: formData.service || "unspecified",
      budget: formData.budget || "unspecified",
      timeline: formData.timeline || "unspecified"
    });

    try {
      const result = await submitContactInquiry(formData, ANTISPAM_TOKEN);

      setStatus({ state: "success", message: COPY.success });
      trackFormEvent("contact_submit_success", {
        submit_mode: result?.mode || "unknown",
        service: formData.service || "unspecified"
      });
      writeStore(LAST_SUBMIT_KEY, String(now));
      writeStore(LAST_PAYLOAD_KEY, dedupeFingerprint);
      setFormData(INITIAL_FORM);
      startedAt.current = Date.now();
    } catch (error) {
      setStatus({
        state: "error",
        message: error.message || "We could not submit your inquiry. Please try again."
      });
      trackFormEvent("contact_submit_error", {
        error_message: error.message || "unknown_error"
      });
    }
  };

  const describedBy = (field, extraIds = []) => {
    const ids = extraIds.filter(Boolean);
    if (errors[field]) ids.push(`${FIELD_IDS[field]}-error`);
    return ids.length ? ids.join(" ") : undefined;
  };

  return (
    <main className={styles.page}>
      {/* Hero — paper. The page title and the real, direct ways to reach the
          team, as hairline-topped cells. .hero reserves the fixed header's
          height above the copy. */}
      <Section tone="paper" size="lg">
        <div className={styles.hero}>
          <div className={styles.intro}>
            <Reveal as={MicroLabel} className={styles.eyebrow} direction="up">
              {COPY.eyebrow}
            </Reveal>
            {/* The Reveal is a wrapper, not the h1 itself: Statement keeps its
                own element and the h1 stays free of animation classes. */}
            <Reveal direction="up" delay={60}>
              <Statement as="h1" size="h2" className={styles.title}>
                {accented(COPY.title)}
              </Statement>
            </Reveal>
            <Reveal as="p" className={styles.lead} direction="up" delay={120}>
              {COPY.lead}
            </Reveal>
          </div>

          <ul className={styles.channels}>
            {CHANNELS.map((channel, index) => (
              <Reveal
                as="li"
                key={channel.label}
                className={styles.channel}
                direction="up"
                delay={Math.min(index, 3) * 60}
              >
                <Icon name={channel.icon} size={22} className={styles.channelIcon} />
                <MicroLabel as="p" className={styles.channelLabel}>
                  {channel.label}
                </MicroLabel>
                {channel.href ? (
                  <a
                    className={`focus-ring ${styles.channelLink}`}
                    href={channel.href}
                    aria-label={channel.ariaLabel}
                    {...(channel.external ? EXTERNAL_LINK_PROPS : {})}
                  >
                    {channel.value}
                  </a>
                ) : (
                  <p className={styles.channelValue}>{channel.value}</p>
                )}
                {channel.note ? <p className={styles.channelNote}>{channel.note}</p> : null}
              </Reveal>
            ))}
          </ul>
        </div>
      </Section>

      {/* Lead form — same fields, same rules, invisible anti-spam. An inset
          olive panel carrying ONE paper ticket. The SectionHead h2 belongs to
          HeadingWipe (App.js): it is never wrapped in a Reveal. */}
      <Section tone="olive" panel size="lg" container="narrow" id="contact-form">
        <SectionHead
          eyebrow={COPY.formEyebrow}
          title={accented(COPY.formTitle)}
          lead={COPY.formLead}
        />

        {/* Reveal and the ticket are separate elements on purpose: nothing on
            the card can then replace Reveal's transition. */}
        <Reveal className={styles.formWrap} direction="up">
          <TicketCard tone="paper" className={styles.formCard}>
            <form
              className={styles.form}
              onSubmit={handleSubmit}
              aria-busy={isSubmitting ? "true" : "false"}
              noValidate
            >
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor={FIELD_IDS.fullName}>
                    Your name
                  </label>
                  <input
                    id={FIELD_IDS.fullName}
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="First and last name"
                    required
                    aria-required="true"
                    aria-invalid={errors.fullName ? "true" : undefined}
                    aria-describedby={describedBy("fullName")}
                  />
                  {errors.fullName ? (
                    <p className={styles.fieldError} id={`${FIELD_IDS.fullName}-error`}>
                      {errors.fullName}
                    </p>
                  ) : null}
                </div>

                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor={FIELD_IDS.workEmail}>
                    Work email
                  </label>
                  <input
                    id={FIELD_IDS.workEmail}
                    name="workEmail"
                    type="email"
                    autoComplete="email"
                    value={formData.workEmail}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    required
                    aria-required="true"
                    aria-invalid={errors.workEmail ? "true" : undefined}
                    aria-describedby={describedBy("workEmail")}
                  />
                  {errors.workEmail ? (
                    <p className={styles.fieldError} id={`${FIELD_IDS.workEmail}-error`}>
                      {errors.workEmail}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor={FIELD_IDS.details}>
                  What do you want to build?
                </label>
                <p className={styles.fieldHint} id={`${FIELD_IDS.details}-hint`}>
                  A sentence or two is enough. We will ask the rest on the call.
                </p>
                <textarea
                  id={FIELD_IDS.details}
                  name="details"
                  rows={5}
                  value={formData.details}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  aria-invalid={errors.details ? "true" : undefined}
                  aria-describedby={describedBy("details", [`${FIELD_IDS.details}-hint`])}
                />
                {errors.details ? (
                  <p className={styles.fieldError} id={`${FIELD_IDS.details}-error`}>
                    {errors.details}
                  </p>
                ) : null}
              </div>

              {/* Honeypot — off-screen, untabbable and hidden from assistive tech.
                  A non-empty value here is treated as spam, client and server side. */}
              <div className={styles.honeypot} aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </label>
              </div>

              <div className={styles.actions}>
                <div className={styles.actionsRow}>
                  {/* The section's one lime pill. */}
                  <button type="submit" className="btn" disabled={isSubmitting}>
                    {isSubmitting ? COPY.sending : COPY.submit}
                  </button>

                  {/* The stated alternative to the form. A .link-arrow on
                      purpose: this section already spends its single lime
                      .btn on the submit button. */}
                  {WA_BRIEF_URL ? (
                    <p className={styles.alt}>
                      <span className={styles.altOr}>or</span>
                      <a
                        className={`link-arrow ${styles.altLink}`}
                        href={WA_BRIEF_URL}
                        aria-label={`${WHATSAPP_LABEL}, opens in a new tab`}
                        {...EXTERNAL_LINK_PROPS}
                      >
                        {WHATSAPP_LABEL}
                      </a>
                    </p>
                  ) : null}
                </div>

                {WA_BRIEF_URL && RESPONSE_NOTE ? (
                  <p className={styles.altNote}>{RESPONSE_NOTE}</p>
                ) : null}

                <p className={styles.privacy}>
                  <Icon name="shield-check" size={18} />
                  {COPY.privacy}
                </p>
              </div>

              {/* Announcement and display are separated on purpose. This region
                  is in the DOM from first paint and never leaves the layout, so
                  "Sending…", success and failure are each announced when they
                  land — a region that is mounted with its message, or hidden
                  with display:none, is unreliable across screen readers. */}
              <p className={styles.srStatus} role="status" aria-live="polite" aria-atomic="true">
                {status.state === "idle" ? "" : status.message}
              </p>

              {/* The visible notice is plain text, not a second live region, so
                  nothing is announced twice. */}
              {status.state === "success" ? (
                <p className={`${styles.notice} ${styles.noticeOk}`}>{status.message}</p>
              ) : null}
              {status.state === "error" ? (
                <p className={`${styles.notice} ${styles.noticeErr}`}>{status.message}</p>
              ) : null}
            </form>
          </TicketCard>
        </Reveal>
      </Section>
    </main>
  );
}

export default ContactPage;
